import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";

import { useDispatch } from "redux/util";
import { SignerLoginPageLayoutsEnum } from "graphql_globals";
import { forgotPassword } from "redux/actions/authentication";
import { useScreenClassInfo } from "common/core/responsive";
import LoadingIndicator from "common/core/loading_indicator";
import OrgBrandTheme from "common/core/brand/org_brand_theme";
import useOrgBrandUnauthed from "common/core/brand/get_org_brand_unauthed";
import UnauthenticatedAppFrame from "common/app_frame/unauthenticated";
import { getAuthenticationTypes } from "common/account/login/util";

import EmailScreen, { type FormValues as EmailSubmitValues } from "./email";
import PasswordScreen, { type FormValues as PasswordSubmitValues } from "./password";
import ForgotPasswordScreen from "./forgot_password";
import SamlScreen, { ErrorMessage as SamlErrorMessage } from "./saml";
import { MfaScreen } from "./mfa";
import type { MfaAuthOption } from "./proof/screens/mfa";
import type { SsoProvider } from "./proof/screens/saml";
import { Background, LoginAttemptsError, PasswordResetLinkSent } from "./components";
import Styles from "./index.module.scss";

type MfaData = {
  auth_options: MfaAuthOption[];
};
type Screen =
  | { type: "email" | "password" | "forgot-password" }
  | { type: "sso"; ssoProvider: SsoProvider }
  | { type: "mfa"; authOptions: MfaAuthOption[]; password: string }
  | null;

type Props = {
  emailFromParams: string;
  loginAttemptsExceeded: boolean;
  onGoogleSignIn: (authCode: string) => void;
  loginWithPassword: (
    values: EmailSubmitValues & PasswordSubmitValues,
  ) => Promise<MfaData | undefined>;
  onClearErrors: () => void;
  webThemeColor?: string | null;
  signerLoginPageLayout?: SignerLoginPageLayoutsEnum;
  onSuccess: () => void;
};

function DeprecatedLogin(props: Props) {
  const {
    onGoogleSignIn,
    loginWithPassword,
    loginAttemptsExceeded,
    onClearErrors,
    emailFromParams,
    webThemeColor,
    signerLoginPageLayout,
    onSuccess,
  } = props;
  const { isMedium } = useScreenClassInfo();

  const [args] = useSearchParams();
  const ssoRequired = args.get("sso") === "required";
  const dispatch = useDispatch();
  const [screen, setScreen] = useState<Screen>(
    emailFromParams ? { type: "password" } : { type: "email" },
  );
  const [email, setEmail] = useState(emailFromParams || "");
  const [password, setPassword] = useState("");
  const [samlError, setSamlError] = useState<string | null>(args.get("samlError"));
  const [authTypeError, setAuthTypeError] = useState(false);
  const [resetPasswordLinkSent, setResetPasswordLinkSent] = useState(false);
  const isEmailScreen = screen?.type === "email";
  const isOktaPluginLogin =
    document.referrer.startsWith("https://notarize.okta.com") && !args.get("samlError");

  const isPasswordScreen = screen?.type === "password";
  const isForgotPasswordScreen = screen?.type === "forgot-password";
  const [authenticationTypes, setAuthenticationTypes] = useState<string[]>([]);
  const [searchParams] = useSearchParams();

  const onEmailSubmit = (formValues: EmailSubmitValues) => {
    if (isOktaPluginLogin) {
      return loginWithPassword({
        email: formValues.email,
        password: formValues.password,
      });
    }
    getAuthenticationTypes({ email: formValues.email })
      .then((resp) => {
        setEmail(formValues.email);
        setAuthenticationTypes(resp.authenticationTypes);
        if (resp.authenticationTypes.includes("sso")) {
          setSamlError(null);
          setScreen({ type: "sso", ssoProvider: resp.ssoProvider! });
        } else if (resp.authenticationTypes.includes("email")) {
          dispatch(forgotPassword(formValues.email));
          setScreen(null);
          setResetPasswordLinkSent(true);
        } else {
          setPassword(formValues.password);
          setScreen({ type: "password" });
        }
      })
      .catch(() => {
        setAuthTypeError(true);
      });
  };

  const onPasswordSubmit = (formValues: PasswordSubmitValues) => {
    return loginWithPassword({ email, password: formValues.password }).then((mfaData) => {
      if (mfaData) {
        setScreen({
          type: "mfa",
          authOptions: mfaData.auth_options,
          password: formValues.password,
        });
      }
    });
  };

  const backToEmailScreen = () => {
    onClearErrors();
    setScreen({ type: "email" });
    if (resetPasswordLinkSent) {
      setResetPasswordLinkSent(false);
    }
  };

  const setForgotPasswordScreen = () => {
    onClearErrors();
    setScreen({ type: "forgot-password" });
  };

  const onLoginAttemptsExceededReset = () => {
    dispatch(forgotPassword(email));
    onClearErrors();
    setResetPasswordLinkSent(true);
    setScreen(null);
  };
  const Forms = (
    <>
      {loginAttemptsExceeded || searchParams.get("forbidden") === "true" ? (
        <LoginAttemptsError
          onBack={backToEmailScreen}
          onResetPassword={onLoginAttemptsExceededReset}
        />
      ) : (
        <>
          {resetPasswordLinkSent && <PasswordResetLinkSent onBack={backToEmailScreen} />}

          {isEmailScreen && (
            <EmailScreen
              isOktaPluginLogin={isOktaPluginLogin}
              onGoogleSignIn={onGoogleSignIn}
              onEmailSubmit={onEmailSubmit}
              authTypeError={authTypeError}
              ssoRequired={ssoRequired}
            />
          )}

          {isPasswordScreen && (
            <PasswordScreen
              email={email}
              emailReminder={Boolean(emailFromParams)}
              onPasswordSubmit={onPasswordSubmit}
              onBack={backToEmailScreen}
              setForgotPasswordScreen={setForgotPasswordScreen}
              password={password}
            />
          )}

          {screen?.type === "sso" && (
            <SamlScreen
              onBack={backToEmailScreen}
              backToPasswordScreen={() => setScreen({ type: "password" })}
              enablePasswordAuth={authenticationTypes.includes("password")}
              email={email}
              ssoProvider={screen.ssoProvider}
            />
          )}

          {samlError && <SamlErrorMessage errorMsg={samlError} />}

          {screen?.type === "mfa" && (
            <MfaScreen
              authOptions={screen.authOptions}
              onBack={backToEmailScreen}
              onSuccess={onSuccess}
              password={screen.password}
            />
          )}

          {isForgotPasswordScreen && <ForgotPasswordScreen onBack={backToEmailScreen} />}

          {ssoRequired && (
            <p className={Styles.error}>
              <FormattedMessage
                id="ec849009-a720-4962-95e7-a00eb12f738c"
                defaultMessage="Google Sign-In failed. You must enter your email and press continue to log into Notarize."
              />
            </p>
          )}
        </>
      )}
    </>
  );

  if (signerLoginPageLayout === SignerLoginPageLayoutsEnum.COVER && !isMedium) {
    return (
      <>
        <div
          className={Styles.loginPageCover}
          style={webThemeColor ? { background: webThemeColor } : {}}
        >
          <div className={Styles.loginCover}>{Forms}</div>
        </div>
      </>
    );
  }

  return (
    <div className={Styles.loginPage}>
      <div className={Styles.loginContent}>
        <div
          className={Styles.loginBackground}
          style={webThemeColor ? { background: webThemeColor } : {}}
        >
          <Background />
        </div>

        <div className={Styles.login}>{Forms}</div>
      </div>
    </div>
  );
}

const ThemedLogin = (props: Props) => {
  const [searchParams] = useSearchParams();
  const transactionId = searchParams.get("transaction_id");
  const organizationName = searchParams.get("organization_name");
  const { brandLoading, brand } = useOrgBrandUnauthed(transactionId);

  if (brandLoading) {
    return <LoadingIndicator />;
  }

  return (
    <OrgBrandTheme theme={brand ?? {}}>
      <UnauthenticatedAppFrame
        orgLogo={brand?.orgLogo}
        orgName={organizationName}
        header={brand?.header}
      >
        <DeprecatedLogin
          {...props}
          signerLoginPageLayout={brand?.signerLoginPageLayout}
          webThemeColor={brand?.webThemeColor}
        />
      </UnauthenticatedAppFrame>
    </OrgBrandTheme>
  );
};

export default ThemedLogin;
